<script setup>
import { PlayerStore } from '@/front/stores/player_store.js'
import { ClickStore } from '@/front/stores/click_store.js'
import { AnnStore } from '@/front/stores/ann_store.js'
import { DesignStore } from '@/front/stores/design_store.js'
import { VideoStore } from '@/front/stores/video_store.js'
import { computed, defineAsyncComponent } from 'vue'
import { Cancel01Icon } from 'hugeicons-vue'
import ProgressBar from 'primevue/progressbar'
import Dialog from 'primevue/dialog'
import { formatSeconds } from '@/front/composables/TimeFormatter.js'

import { finishLink } from '@/front/composables/ClickComposer.js'
// Store initialization
const playerStore = PlayerStore()
const clickStore = ClickStore()
const designStore = DesignStore()
const annStore = AnnStore()
const videoStore = VideoStore()
import { useGlobalRenderTracker } from '@/front/composables/GlobalRenderTracker.js'
if (playerStore.isDebugPerformance) {
  useGlobalRenderTracker('DialogLayer', { logLevel: 'none' })
}

const modalLink = computed(() => clickStore.activeLink || '')
const modalLabel = computed(() => {
  if (!modalLink.value) return ''
  try {
    const parsed = new URL(modalLink.value)
    const fileName = decodeURIComponent(parsed.pathname.split('/').pop() || '')
    return fileName || parsed.hostname
  } catch (_error) {
    return modalLink.value
  }
})
const isPdfModal = computed(() => /\.pdf(?:[?#]|$)/i.test(modalLink.value))

function openModalLinkInNewTab() {
  if (!modalLink.value) return
  const newWindow = window.open(modalLink.value, '_blank', 'noopener,noreferrer')
  if (newWindow) {
    newWindow.opener = null
  }
}

import MessageLayer from '@/front/components/MessageLayer.vue'
import QuestionLayer from '@/front/components/QuestionLayer.vue'

// const MagicMenuLayer = defineAsyncComponent(() => 
//   import('@/front/components/MagicMenuLayer.vue')
// )
const MagicMenuLayer = defineAsyncComponent(() => 
  import('@/front/components/MagicMenuLayer.vue')
)
const ImageLayer = defineAsyncComponent(() => 
  import('@/front/components/ImageLayer.vue')
)
const ArticleLayer = defineAsyncComponent(() => 
  import('@/front/components/ArticleLayer.vue')
)
const GenieLayer = defineAsyncComponent(() => 
  import('@/front/components/GenieLayer.vue')
)
const ChatGPTLayer = defineAsyncComponent(() => 
  import('@/front/components/ChatGPTLayer.vue')
)
const CollectLayer = defineAsyncComponent(() => 
  import('@/front/components/CollectLayer.vue')
)
// const ClipLayer = defineAsyncComponent(() => 
  // import('@/front/components/ClipLayer.vue')
// )
// const AudioLayer = defineAsyncComponent(() => 
//   import('@/front/components/AudioLayer.vue')
// )
import AudioLayer from '@/front/components/AudioLayer.vue'
import ClipLayer from '@/front/components/ClipLayer.vue'

const DrawViewLayer = defineAsyncComponent(() => 
  import('@/front/components/DrawViewLayer.vue')
)
const CaptureLeadLayer = defineAsyncComponent(() => 
  import('@/front/components/CaptureLeadLayer.vue')
)
const ParticipateLayer = defineAsyncComponent(() => 
  import('@/front/components/ParticipateLayer.vue')
)

</script>

<template>
  <Transition :name="clickStore.questionActive ? clickStore.questionActive.transition || 'fade' : 'none'" mode="out-in" appear style="--animate-duration: 0.5s;">
    <QuestionLayer v-if="clickStore.questionActive && !clickStore.clipActive && !clickStore.magicMenuActive" :readonly="annStore.isEditing || annStore.isBulkEditing"  :style="{ opacity: clickStore.activeMessage ? 0 : 1 }"/>
  </Transition>

  <Transition :name="videoStore.video.design.message_transition || 'fade'" mode="in" appear :style="{ '--animate-duration': clickStore.questionActive ? '10ms' : '0.5s' }">
    <MessageLayer v-if="clickStore.activeMessage" />
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
      <ImageLayer v-if="clickStore.activeImageUrl"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <ArticleLayer v-if="clickStore.activeArticle"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <GenieLayer v-if="clickStore.genieActive"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <ChatGPTLayer v-if="clickStore.chatGPTActive"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <CollectLayer v-if="clickStore.activeCollect"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <Dialog v-model:visible="clickStore.participateActive" modal :header="$t('common.add_comment')" class="w-[90%] max-w-xl">
      <ParticipateLayer/>
    </Dialog>
  </Transition>

  <!-- Used for playback - editing is in -->
  <!-- <Suspense v-if="clickStore.magicMenuActive && !clickStore.activeMessage">
    <MagicMenuLayer />
  </Suspense> -->


  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <ClipLayer v-if="!annStore.isEditing && (clickStore.clipActive || clickStore.interactionClipActive)" :ann="clickStore.activeClipAnn"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <AudioLayer v-if="clickStore.audioActive"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <CaptureLeadLayer v-if="clickStore.captureLeadActive"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <DrawViewLayer v-if="clickStore.activeDrawing" class="w-full h-full"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <div v-if="clickStore.activeTimedPause && videoStore.video?.design.show_countdown_bar" id="pause-progress" class="w-full absolute top-0 left-0 w-full bg-white/30 h-4 @md/player:h-6 @lg/player:h-8">
      <div class="absolute top-0 left-0 w-full h-full" :style="{ width: clickStore.activePauseAmounts.progress + '%', backgroundColor: videoStore.video.design.color }"></div>
      <div class="absolute top-0 left-0 w-full h-full flex items-center justify-center text-white text-lg font-bold">
        {{ formatSeconds(clickStore.activePauseAmounts.remaining/1000, false) }}
      </div>
    </div>
  </Transition>

  <!-- Modal embed -->
  <Dialog
    id="link-dialog"
    v-model:visible="clickStore.activeLink"
    modal
    :showHeader="false"
    :draggable="false"
    class="link-dialog w-[92vw] max-w-7xl select-none"
  >
    <div class="relative h-[92vh] w-full overflow-hidden rounded-xl bg-[#141414]">
      <iframe
        :src="modalLink"
        :class="[
          'absolute inset-x-0 bottom-0 top-0 w-full border-0 h-full',
          isPdfModal ? 'bg-[#1f1f1f]' : 'bg-white',
        ]"
        referrerpolicy="no-referrer-when-downgrade"
      />

      <div class="absolute right-3 top-3 z-20">
        <button
          @click.stop="finishLink"
          class="rounded-full border border-white/30 bg-[var(--user-theme-color)] p-1 h-9 w-9"
        >
          <Cancel01Icon class="mx-auto h-[80%] w-[80%] aspect-square text-white" />
        </button>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.loading-state {
  @apply w-full h-full flex items-center justify-center;
  min-height: 50px;
}
</style>

<style>
/* Override default primevue dialog header */
#link-dialog [data-pc-section="header"] {
  @apply !hidden;
}
#link-dialog [data-pc-section="content"] {
  @apply !bg-transparent !p-0 !overflow-hidden !h-[92vh] !max-h-[92vh];
}
</style>
